// @ts-nocheck
/* eslint-disable */
import React, { lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ModernAuthentication from "./components/ModernAuthentication";
import { UserSettings } from "./pages/UserSettings";
import Modal from "./components/Modal/ModalGlobal";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import NotFound from "./pages/NotFound";
import { Provider } from "react-redux";
import { store } from "./store";
import VerticalNav from "./pages/VerticalNav";
import Dashboard from "./pages/Dashboard";
import Home from "./pages/Home";
import GroupPage from "./pages/GroupPage";
import ModelsPage from "./pages/ModelsPage";
import OperationsPage from "./pages/OperationsPage";
// Model Templates page: browse and deploy published templates. Distinct from ModelGardenPage below (the admin CRUD screen for authoring templates).
import ModelTemplatesPage from "./pages/ModelGarden";
import DatasetsPage from "./pages/DatasetsPage";
import DataUpload from "./pages/DataUpload";
import DataUploadForm from "./pages/DataUploadForm";
import { LoggedInDisplay } from "./components/AuthenticationComponents";
import PerformanceCurvePage from "./pages/PerformanceCurvePage";
import ReactGA from "react-ga4";
import { RealTimeDesk } from "./newComponents/RealTimeDeskComponent/RealTimeDesk";

import { configureAmplify } from "./config/amplifyOutputs";
import DataUploadEditForm from "./pages/DataUploadEditForm";
import ExplorePage from "./pages/ExplorePage";
import AnalysisListPage from "./pages/AnalysisListPage";
import OrganizationPage from "./pages/OrganizationPage";
import QuickSetupPage from "./pages/QuickSetupPage";
import UploadPage from "./pages/UploadPage";
import CrudApiTestPage from "./pages/CrudApiTestPage";
import ResourcesPage from "./pages/ResourcesPage";
import ApiDocsPage from "./pages/Resources/ApiDocsPage";
import UserGuidePage from "./pages/Resources/UserGuidePage";
import ToolsPage from "./pages/Tools";
import InsightsPage from "./pages/InsightsPage";
import ChartDemo from "./components/DataVisualization/ChartDemo";
import CompareAsset from "./newComponents/CompareAsset/CompareAsset";
import PinToDashboardModal from "./components/Modal/PinToDashboardModal";
import AssetDashboard from "./newComponents/NewAssetDashboard/AssetDashboard";
import AssetPage from "./newComponents/NewAssetPage/AssetPage";
import NewDashboard from "./newComponents/MainDashboard/NewDashboard";

import OnboardingV2 from "./pages/OnboardingV2"; // Onboarding workflow Added by Mike T
import RequireOnboarding from "./components/Onboarding/RequireOnboarding"; // Onboarding workflow Added by Mike T

import ComposedForecastPage from "./pages/ComposedForecastPage";
import AiAnalystPage from "./pages/aiAnalystpage/AiAnalystPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import ProjectsPage from "./pages/Projects";
import ProjectDetailPage from "./pages/Projects/ProjectDetailPage";
import ManagePublicProfilePage from "./pages/ManagePublicProfilePage";
import ManageOrgPublicProfilePage from "./pages/ManageOrgPublicProfilePage";
import PublicProfilesPage from "./pages/PublicProfilesPage";
import SharedConversationPage from "./pages/sharedConversation/SharedConversationPage";
import EmbedConversationPage from "./pages/sharedConversation/EmbedConversationPage";
import { STATUS_PAGE_URL } from "./config/appConfig";


// sessionStorage throws outright where storage is blocked (lockdown mode, partitioned iframes) — treat that as "already reloaded" so we never loop
const hasReloadedForChunk = () => {
  try {
    return sessionStorage.getItem("chunkReloaded") === "true";
  } catch {
    return true;
  }
};

// Best-effort: a blocked store just costs us the one-shot guard, it must never break the page
const setReloadedForChunk = (reloaded) => {
  try {
    if (reloaded) sessionStorage.setItem("chunkReloaded", "true");
    else sessionStorage.removeItem("chunkReloaded");
  } catch {
    // storage unavailable; nothing to record
  }
};

// A deploy replaces hashed chunk files, so an open tab can request one that no longer exists — reload once to pick up the new index.html
const lazyRoute = (importFn) =>
  lazy(async () => {
    let module;
    try {
      module = await importFn();
    } catch {
      try {
        // A transient network blip shouldn't cost a full reload — retry the fetch once first
        module = await importFn();
      } catch (error) {
        // Offline, a reload swaps the running app for the browser's offline page — let the boundary show a recoverable message instead
        if (!navigator.onLine || hasReloadedForChunk()) throw error;
        setReloadedForChunk(true);
        window.location.reload();
        return new Promise(() => {}); // never resolves; the reload takes over
      }
    }
    setReloadedForChunk(false); // outside the try so a storage throw can't fail a chunk that loaded fine
    return module;
  });

// Admin routes are split into their own chunk so non-admin users never download admin code
const AdminPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/AdminPage"));
const ScrapersPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/ScrapersPage"));
const ModelGardenPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/ModelGardenPage"));
const SystemPromptsPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/SystemPromptsPage"));
const AccessRequestsPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/AccessRequestsPage"));
const OrgChatAccessPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/OrgChatAccessPage"));
const ErrorLogsPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/ErrorLogsPage"));
const ProcessedModelRunHistoryPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/ProcessedModelRunHistoryPage"));
const ProcessedModelSchemasAdminPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/ProcessedModelSchemasAdminPage"));
const ProcessedModelSchemaRunHistoryPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/ProcessedModelSchemaRunHistoryPage"));
const UserTokenGrantsPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/UserTokenGrantsPage"));
const ProcessedModelV1EmailAdminPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/ProcessedModelV1EmailAdminPage"));
const AdminUserRecordsPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/AdminUserRecordsPage"));
const DatasourceMetadataPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/DatasourceMetadataPage"));
const McpUsageCheckPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/McpUsageCheckPage"));
const AdminSubscriptionDriftPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/AdminSubscriptionDriftPage"));
const ModalSizeTesterPage = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./pages/ModalSizeTesterPage"));

// Member tools load on demand too: the Tools index is a tiny card page, but each tool brings its own data layer
const ForecastEmailSchedulesPage = lazyRoute(() => import(/* webpackChunkName: "tools" */ "./pages/ForecastEmailSchedulesPage"));
const DeliveryLogsPage = lazyRoute(() => import(/* webpackChunkName: "tools" */ "./pages/DeliveryLogsPage"));
const DeliveryLogDetailPage = lazyRoute(() => import(/* webpackChunkName: "tools" */ "./pages/DeliveryLogDetailPage"));
const ForecastVerificationPage = lazyRoute(() => import(/* webpackChunkName: "forecast-verification" */ "./pages/ForecastVerificationPage"));
const ForecastReportFormPage = lazyRoute(() => import(/* webpackChunkName: "forecast-verification" */ "./pages/ForecastReportFormPage"));
const ForecastReportDetailPage = lazyRoute(() => import(/* webpackChunkName: "forecast-verification" */ "./pages/ForecastReportDetailPage"));
const WatershedDelineationPage = lazyRoute(() => import(/* webpackChunkName: "watershed" */ "./pages/WatershedDelineationPage"));

configureAmplify();

const queryClient = new QueryClient();

// Convenience alias: app.erode.ai/status → hosted Better Stack status page
const StatusRedirect = () => {
  React.useEffect(() => {
    window.location.replace(STATUS_PAGE_URL);
  }, []);
  return null;
};

const GAtrackingID = process.env.REACT_APP_GA_TRACKING_ID ?? "false";
console.log("track", process.env);
ReactGA.initialize(GAtrackingID);

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
        <BrowserRouter>
          <Modal />
          <Routes>
            {/* new layout */}
            {/* <Route path="/test" element={<VerticalNav />}> <Route path="/test" element={<Dashboard />} /> </Route> */}
            <Route path="/" element={<ModernAuthentication />} />
            {/* Public, read-only shared conversation view — no auth guard */}
            <Route path="/share/:shareId" element={<SharedConversationPage />} />
            {/* Public, lean iframe-embeddable live result view — no auth guard */}
            <Route path="/embed/:shareId" element={<EmbedConversationPage />} />
            {/* Onboarding workflow Added by Mike T */}
            <Route path="/onboarding-v2" element={<OnboardingV2 />} />
            <Route path="/status" element={<StatusRedirect />} />
            <Route
              path="/home"
              element={
                // <RequireOnboarding>
                <VerticalNav />
                // </RequireOnboarding>
              }
            >
              <Route path="/home" element={<Home />} />
              <Route path="/home/group/:groupid" element={<GroupPage />} />
              <Route
                path="/home/analysis"
                element={<AnalysisListPage />}
              />
              <Route
                path="/home/analysis/new"
                element={<ExplorePage />} //Rename Explore Later
              />
              {/* <Route path="/home/group/:groupid/asset/:assetid/explore" element={<ExplorePage />} /> */}
              <Route
                path="/home/group/:groupid/asset/:assetid"
                element={<Dashboard />}
              />
              <Route
                path="/home/group/:groupid/historical"
                element={<div>Historical</div>}
              />
              <Route path="/home/pcurve" element={<PerformanceCurvePage />} />
              {/* Model Templates owns /home/model-templates; the admin schema manager lives at /home/model-schemas. Old paths redirect so bookmarks keep working. */}
              <Route path="/home/model-templates" element={<ModelTemplatesPage />} />
              <Route path="/home/models" element={<Navigate to="/home/model-templates" replace />} />
              <Route path="/home/model-garden" element={<Navigate to="/home/model-templates" replace />} />
              <Route path="/home/model-schemas" element={<ModelsPage />} />
              <Route path="/home/data-models" element={<DatasetsPage />} />
              <Route path="/home/datasets" element={<Navigate to="/home/data-models" replace />} />
              <Route path="/home/dashboards" element={<OperationsPage />} />
              <Route path="/home/dashboard" element={<Navigate to="/home/dashboards" replace />} />
              <Route path="/home/operations" element={<Navigate to="/home/dashboards" replace />} />
              <Route path="/home/settings" element={<LoggedInDisplay />} />
              <Route path="/home/dataupload" element={<DataUpload />} />
              <Route path="/home/dataupload/new" element={<DataUploadForm />} />
              <Route
                path="/home/dataupload/:custommodelid"
                element={<DataUploadEditForm />}
              />
              <Route
                path="/home/organization/:organizationId"
                element={<OrganizationPage />}
              />
              <Route
                path="/home/quicksetup"
                element={<QuickSetupPage />}
              />
              <Route
                path="/home/upload"
                element={<UploadPage />}
              />
              <Route
                path="/home/resources"
                element={<ResourcesPage/>}
              />
              <Route
                path="/home/resources/api-docs"
                element={<ApiDocsPage />}
              />
              <Route
                path="/home/resources/user-guide"
                element={<UserGuidePage />}
              />
              <Route path="/home/tools" element={<ToolsPage />} />
              <Route path="/home/email-schedule" element={<ForecastEmailSchedulesPage />} />
              <Route path="/home/delivery-logs" element={<DeliveryLogsPage />} />
              <Route path="/home/delivery-logs/:processedModelId" element={<DeliveryLogDetailPage />} />
              <Route path="/home/watershed-delineation" element={<WatershedDelineationPage />} />
              <Route path="/home/forecast-verification" element={<ForecastVerificationPage />} />
              <Route path="/home/forecast-verification/new" element={<ForecastReportFormPage />} />
              <Route path="/home/forecast-verification/:reportId" element={<ForecastReportDetailPage />} />
              <Route path="/home/forecast-verification/:reportId/edit" element={<ForecastReportFormPage />} />
              <Route path="/home/insights" element={<InsightsPage />} />
              <Route path="/home/ai-analyst" element={<AiAnalystPage />} />
              <Route path="/home/ai-analyst/:conversationId" element={<AiAnalystPage />} />
              <Route path="/home/projects" element={<ProjectsPage />} />
              <Route path="/home/projects/:projectid" element={<ProjectDetailPage />} />
              <Route path="chart-demo" element={<ChartDemo />} />
              <Route path="/home/admin" element={<AdminPage />} />
              <Route path="/home/admin/scrapers" element={<ScrapersPage />} />
              <Route path="/home/admin/modelgarden" element={<ModelGardenPage />} />
              <Route path="/home/admin/systemprompts" element={<SystemPromptsPage />} />
              <Route path="/home/admin/access-requests" element={<AccessRequestsPage />} />
              <Route path="/home/admin/ai-analyst-access" element={<OrgChatAccessPage />} />
              <Route path="/home/admin/error-logs" element={<ErrorLogsPage />} />
              <Route path="/home/admin/run-history" element={<ProcessedModelRunHistoryPage />} />
              <Route path="/home/admin/processed-models" element={<ProcessedModelSchemasAdminPage />} />
              <Route path="/home/admin/processed-models/:processedModelSchemaId" element={<ProcessedModelSchemaRunHistoryPage />} />
              <Route path="/home/admin/v1-forecast-emails" element={<ProcessedModelV1EmailAdminPage />} />
              {/* The V1 delivery log moved to Tools (/home/delivery-logs); old admin links still land on it */}
              <Route path="/home/admin/v1-delivery" element={<Navigate to="/home/delivery-logs" replace />} />
              <Route path="/home/admin/v1-delivery/:processedModelId" element={<DeliveryLogDetailPage />} />
              <Route path="/home/admin/token-grants" element={<UserTokenGrantsPage />} />
              <Route path="/home/admin/user-records" element={<AdminUserRecordsPage />} />
              <Route path="/home/admin/datasource-metadata" element={<DatasourceMetadataPage />} />
              {/* Unadvertised diagnostic — MCPUsage owner-auth check (native/Google/admin). Not in any nav. */}
              <Route path="/home/mcp-usage" element={<McpUsageCheckPage />} />
              <Route path="/home/admin/subscription-drift" element={<AdminSubscriptionDriftPage />} />
              <Route path="/home/admin/modal-size-tester" element={<ModalSizeTesterPage />} />
              <Route path="/home/custom-views" element={<ComposedForecastPage />} />
              <Route path="/home/forecast-views" element={<Navigate to="/home/custom-views" replace />} />
              <Route path="/home/composed-forecasts" element={<Navigate to="/home/custom-views" replace />} />
              <Route path="/home/realtime-desk" element={<RealTimeDesk />} />
              <Route path="/home/compare-assets" element={<CompareAsset />} />
              <Route path="/home/pin-to-dashboard" element={<PinToDashboardModal />} />
              <Route path="/home/asset-dashboard" element={<AssetDashboard />} />
              <Route path="/home/asset-page/:groupid/:assetid" element={<AssetPage />} />
              <Route path="/home/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/home/account/public-profile" element={<ManagePublicProfilePage />} />
              <Route path="/home/organization/:organizationId/public-profile" element={<ManageOrgPublicProfilePage />} />
              <Route path="/home/public-profiles" element={<PublicProfilesPage />} />
              <Route path="/home/asset-dashboard/:groupid" element={<AssetDashboard />} /> {/*For testing */}
              <Route path="/home/new-dashboard" element={<NewDashboard />} />
              {/* <Route path="/home/crudapitest" element={<CrudApiTestPage />} /> */}
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
